/**
 * 红色商务模板 - 首页（v3）
 * 
 * 设计核心理念：参考58同城/Yellow Pages 分类信息网站
 * - 无Hero装饰，直接展示内容
 * - 信息以数据表格形式呈现
 * - 高信息密度，目录风格
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/red/components/Navbar";
import Footer from "@/templates/red/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function RedHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);
  const [tableSort, setTableSort] = useState<"latest" | "views">("latest");

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 10)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 30 })
      .then((data) => {
        setListings(data.list || []);
        setTotalListings(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const sortedListings = [...listings].sort((a, b) => {
    if (tableSort === "views") return (b.view_count || 0) - (a.view_count || 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navbar />

      {/* pt-20 给固定导航栏让位，紧凑搜索+统计 */}
      <div className="bg-white border-b border-gray-200 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* 搜索框 */}
            <form onSubmit={handleSearch} className="flex-1 w-full sm:max-w-xl">
              <div className="relative flex items-center border-2 border-red-500 rounded-md overflow-hidden bg-white">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="输入关键词搜索..."
                  className="flex-1 py-1.5 px-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
                />
                <button
                  type="submit"
                  className="bg-red-600 text-white px-4 py-1.5 text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-search-line" />
                </button>
              </div>
              {showSearchTip && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {searchConfig.disabledMessage || "搜索功能已关闭"}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── 左侧：分类导航 + 发布入口 ── */}
          <aside className="w-full lg:w-48 shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                  <i className="ri-menu-line text-red-500" />
                  信息分类
                </h3>
              </div>
              <div className="divide-y divide-gray-50">
                <Link
                  to="/category"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <i className="ri-global-line text-gray-400 text-xs" />
                  全部分类
                  <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{totalListings}</span>
                </Link>
                {loadingCategories
                  ? [...Array(8)].map((_, i) => (
                      <div key={i} className="px-4 py-2.5 animate-pulse flex items-center gap-2">
                        <div className="w-3 h-3 bg-gray-200 rounded" />
                        <div className="w-16 h-3 bg-gray-200 rounded" />
                      </div>
                    ))
                  : categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <i className={`${cat.icon || 'ri-folder-line'} text-gray-400 text-xs`} />
                        <span className="truncate">{cat.name}</span>
                      </Link>
                    ))}
              </div>
            </div>

            <div className="mt-3 bg-red-600 rounded-lg p-3 text-center">
              <i className="ri-edit-circle-line text-white text-xl block mb-1" />
              <p className="text-white text-xs font-medium mb-2">发布信息</p>
              <Link
                to="/publish"
                className="block w-full py-1.5 bg-white text-red-600 text-xs font-bold rounded hover:bg-red-50 transition-colors"
              >
                立即发布
              </Link>
            </div>
          </aside>

          {/* ── 右侧：主内容区 ── */}
          <div className="flex-1 min-w-0">
            {/* ── 数据表格 ── */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                  <i className="ri-list-check text-red-500" />
                  最新信息
                </h2>
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => setTableSort("latest")}
                    className={`px-2.5 py-1 text-xs rounded transition-colors cursor-pointer ${
                      tableSort === "latest" ? "bg-red-600 text-white" : "text-gray-500 hover:text-red-600"
                    }`}
                  >
                    <i className="ri-time-line mr-0.5" />最新
                  </button>
                  <button
                    onClick={() => setTableSort("views")}
                    className={`px-2.5 py-1 text-xs rounded transition-colors cursor-pointer ${
                      tableSort === "views" ? "bg-red-600 text-white" : "text-gray-500 hover:text-red-600"
                    }`}
                  >
                    <i className="ri-fire-line mr-0.5" />最热
                  </button>
                </div>
              </div>

              {loadingListings ? (
                <div className="p-6 space-y-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center gap-4 py-3 border-b border-gray-50 last:border-0">
                      <div className="w-12 h-12 bg-gray-200 rounded shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="w-1/2 h-5 bg-gray-200 rounded" />
                        <div className="w-1/3 h-3.5 bg-gray-100 rounded" />
                      </div>
                      <div className="w-16 h-3.5 bg-gray-100 rounded hidden sm:block" />
                      <div className="w-14 h-3.5 bg-gray-100 rounded hidden md:block" />
                    </div>
                  ))}
                </div>
              ) : sortedListings.length > 0 ? (
                <div className="overflow-x-auto">
                  {/* 桌面表格 */}
                  <table className="w-full hidden md:table">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/30">
                        <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-10">#</th>
                        <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5">信息标题</th>
                        <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-24">分类</th>
                        <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-20">联系人</th>
                        <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-24">
                          {tableSort === "views" ? "浏览" : "发布时间"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {sortedListings.map((listing, index) => (
                        <tr
                          key={listing.id}
                          className={`group hover:bg-red-50/30 transition-colors cursor-pointer ${
                            listing.is_pinned ? "bg-amber-50/30" : ""
                          }`}
                          onClick={() => navigate(`/listing/${listing.id}`)}
                        >
                          <td className="px-4 py-3.5">
                            {listing.is_pinned ? (
                              <span className="w-7 h-7 bg-red-600 text-white text-xs font-bold rounded flex items-center justify-center">
                                顶
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 pl-1">{index + 1}</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                                {listing.images?.[0] ? (
                                  <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <i className="ri-image-line text-gray-300 text-xl" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 group-hover:text-red-700 transition-colors truncate max-w-xs lg:max-w-lg">
                                  {listing.title}
                                </p>
                                <p className="text-xs text-gray-400 truncate max-w-xs lg:max-w-lg mt-0.5">
                                  {listing.content?.substring(0, 80) || "暂无描述"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                              {listing.category?.name || "未分类"}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                            {listing.contact_name || "匿名"}
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                            {tableSort === "views" ? `${listing.view_count} 次` : formatTableDate(listing.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* 移动端列表 */}
                  <div className="md:hidden divide-y divide-gray-50">
                    {sortedListings.map((listing) => (
                      <div
                        key={listing.id}
                        className={`flex items-start gap-3 px-4 py-3.5 hover:bg-red-50/20 transition-colors cursor-pointer ${
                          listing.is_pinned ? "bg-amber-50/20" : ""
                        }`}
                        onClick={() => navigate(`/listing/${listing.id}`)}
                      >
                        <div className="w-14 h-14 rounded bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                          {listing.images?.[0] ? (
                            <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-image-line text-gray-300 text-xl" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {listing.is_pinned && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-600 text-white rounded font-bold shrink-0">顶</span>
                            )}
                            <p className="text-sm font-medium text-gray-800 truncate">{listing.title}</p>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 line-clamp-1">{listing.content?.substring(0, 60) || "暂无描述"}</p>
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-400">
                            <span>{listing.category?.name || "未分类"}</span>
                            <span>·</span>
                            <span>{listing.contact_name || "匿名"}</span>
                            <span>·</span>
                            <span>{formatTableDate(listing.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-14">
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-gray-100">
                    <i className="ri-inbox-line text-xl text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">暂无信息</p>
                  <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
                </div>
              )}

              {sortedListings.length > 0 && (
                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/30 flex items-center justify-between">
                  <span className="text-xs text-gray-400">共 {totalListings} 条信息</span>
                  <Link to="/category" className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                    查看全部 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function formatTableDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
